import { DiagnosticsHeader } from "./DiagnosticsHeader";
import type { DiagnosticsTargetOption } from "./DiagnosticsTargetPicker.logic";
import { DiagnosticsSettingsContent } from "./DiagnosticsSettingsContent";
import { SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { useDiagnosticsPanelState } from "./useDiagnosticsPanelState";
import { DiagnosticsPrototypeVariantA } from "./diagnosticsPrototype/DiagnosticsPrototypeVariantA";
import { DiagnosticsPrototypeVariantB } from "./diagnosticsPrototype/DiagnosticsPrototypeVariantB";
import { DiagnosticsPrototypeVariantC } from "./diagnosticsPrototype/DiagnosticsPrototypeVariantC";
import { DiagnosticsPrototypeVariantD } from "./diagnosticsPrototype/DiagnosticsPrototypeVariantD";
import { PrototypeSwitcher } from "./diagnosticsPrototype/PrototypeSwitcher";
import { diagnosticsPrototypeModelFromState } from "./diagnosticsPrototype/useDiagnosticsPrototypeModel";
import { useDiagnosticsPrototypeVariant } from "./diagnosticsPrototype/useDiagnosticsPrototypeVariant";

export { buildProcessTableRows } from "./DiagnosticsProcessTable.logic";

/**
 * Diagnostics on the settings standard.
 *
 * It used to bring its own shell: a 1440px column, its own card family, its
 * own heading scale, and — for the Cloud target only — a two-pane grid. Three
 * of Settings' pages already composed from `SettingsPageContainer`;
 * this is now a fourth, so a reader moving between Devices and Diagnostics
 * sees the same width, the same section rhythm, and the same heading weight,
 * and every target class renders as one column of sections rather than each
 * inventing a layout.
 */
export function DiagnosticsSection() {
  const state = useDiagnosticsPanelState();
  // PROTOTYPE: three layouts behind ?variant=A|B|C in dev builds only.
  // Remove with the diagnosticsPrototype folder once a direction is chosen.
  const [variant, setVariant] = useDiagnosticsPrototypeVariant();
  const prototypeModel = variant === null ? null : diagnosticsPrototypeModelFromState(state);
  const selectTarget = (option: DiagnosticsTargetOption) => {
    if (option.environmentId !== null) {
      state.cloud.diagnostics.selectEnvironment(option.environmentId);
    }
    state.tabs.setActiveTab(option.tab);
  };

  if (variant !== null && prototypeModel !== null) {
    const props = {
      model: prototypeModel,
      targets: { groups: state.targetGroups, active: state.activeTarget, onSelect: selectTarget },
      refresh: {
        label: state.cloud.isPlatformAdmin ? "Probe" : "Refresh",
        onRefresh: state.recheck,
        refreshing: state.refreshing,
        canRefresh: state.canRefresh,
      },
    };
    if (variant === "D") {
      return (
        <>
          <DiagnosticsPrototypeVariantD {...props} />
          <PrototypeSwitcher current={variant} onChange={setVariant} />
        </>
      );
    }
    return (
      <SettingsSection {...searchableSetting("general-diagnostics")}>
        {variant === "A" ? <DiagnosticsPrototypeVariantA {...props} /> : null}
        {variant === "B" ? <DiagnosticsPrototypeVariantB {...props} /> : null}
        {variant === "C" ? <DiagnosticsPrototypeVariantC {...props} /> : null}
        <PrototypeSwitcher current={variant} onChange={setVariant} />
      </SettingsSection>
    );
  }

  return (
    <SettingsSection {...searchableSetting("general-diagnostics")}>
      <DiagnosticsHeader
        onRefresh={state.recheck}
        refreshing={state.refreshing}
        canRefresh={state.canRefresh}
        isPlatformAdmin={state.cloud.isPlatformAdmin}
        targetGroups={state.targetGroups}
        activeTarget={state.activeTarget}
        // A cloud option carries the sprite the Cloud panel must also hold,
        // so the picker and that panel's list never disagree.
        onSelectTarget={selectTarget}
        eventsActive={state.tabs.activeTab === "events"}
        onShowRuntime={state.tabs.showRuntimeTab}
        onShowEvents={() => state.tabs.setActiveTab("events")}
      />
      <DiagnosticsSettingsContent
        activeTab={state.tabs.activeTab}
        desktopTargets={state.topology.desktopTargets}
        canonical={state.canonical}
        cloud={state.cloud}
        server={state.server}
      />
    </SettingsSection>
  );
}
